import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "@crema/utility/RichTextEditor";
import { AppState } from "redux/store";
import {
  getInvestorDeskSections,
  updateInvestorDeskSection,
} from "redux/actions";
import { InvestorDeskReport } from "types/models/investorDeskMaster/InvestorDeskMasterApp";

const GrowthRoadmapPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { sections } = useSelector<AppState, AppState["investorDeskMaster"]>(
    ({ investorDeskMaster }) => investorDeskMaster
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [title, setTitle] = useState("Growth Roadmap");
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    setIsLoading(true);
    dispatch(getInvestorDeskSections());
  }, [dispatch]);

  useEffect(() => {
    if (sections && sections.length > 0) {
      setIsLoading(false);
      const matched = sections.find((item: InvestorDeskReport) => {
        const t = (item?.title || "").toLowerCase();
        return t.includes("growth roadmap") || item.id === 4;
      });

      if (matched) {
        setSectionId(matched.id);
        setTitle(matched.title || "Growth Roadmap");
        setHeading(matched.heading || "");
        setContent(matched.content || "");
      }
    }
  }, [sections]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim() || !content.trim()) {
      setSnackbar({
        open: true,
        message: "Please fill in heading and content",
        severity: "error",
      });
      return;
    }

    if (!sectionId) {
      setSnackbar({
        open: true,
        message: "Growth Roadmap section not found to update",
        severity: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(
        updateInvestorDeskSection(sectionId, {
          title: title || "Growth Roadmap",
          heading,
          content,
          status: "active",
        }) as any
      );
      setSnackbar({
        open: true,
        message: "Growth roadmap updated successfully",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to update growth roadmap",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box >
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TrendingUpOutlinedIcon
            sx={{ fontSize: { xs: 28, md: 34 }, color: "primary.main" }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.4rem", md: "1.75rem" },
              letterSpacing: "-0.02em",
            }}
          >
            Edit Growth Roadmap
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.5, ml: { xs: 5, md: 6 } }}
        >
          Update growth roadmap information and milestones
        </Typography>
      </Box>

      {/* Main Card */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Accordion
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  overflow: "hidden",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: "#fafafa",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 3,
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                      Growth Roadmap Content
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Heading and content for growth roadmap
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: { xs: 2.5, md: 4 } }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
                    {/* Heading Field */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                          color: "#374151",
                        }}
                      >
                        Heading <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#fff",
                          "& .quill": { height: "auto" },
                          "& .ql-container": {
                            minHeight: "90px",
                            fontSize: 14,
                          },
                        }}
                      >
                        <RichTextEditor
                          value={heading}
                          onChange={(val) => setHeading(val)}
                        />
                      </Box>
                    </Box>

                    {/* Content Field */}
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 600,
                          mb: 1,
                          color: "#374151",
                        }}
                      >
                        Content <span style={{ color: "#ef4444" }}>*</span>
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                          overflow: "hidden",
                          bgcolor: "#fff",
                          "& .quill": { height: "auto" },
                          "& .ql-container": {
                            minHeight: "180px",
                            fontSize: 14,
                          },
                        }}
                      >
                        <RichTextEditor
                          value={content}
                          onChange={(val) => setContent(val)}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 4,
                      pt: 3,
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isSaving}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSaving}
                      startIcon={
                        isSaving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3.5,
                        fontWeight: 600,
                      }}
                    >
                      {isSaving ? "Updating..." : "Update"}
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GrowthRoadmapPage;
